import { useCallback, useMemo } from "react";

import type { FilterName, FilterState } from "@components";

import {
  getDateRangeForQuery,
  isDateRangeThisWeek,
  isDateRangeToday,
} from "@helpers/utils";

import type { BaseQueryState, FieldFilters } from "../utils/queryStateUtils";

type UseFiltersOptions<T extends object> = {
  queryState: BaseQueryState<T>;
  updateQueryState: (newState: BaseQueryState<T>) => void;
  currentUserId: string | undefined;
  fieldNames?: {
    /** Field used for the "mine" filter. Defaults to "creator". */
    creator?: string & keyof T;
    /** Field used for date range filters (today/week). Defaults to "created". */
    date?: string & keyof T;
    /** Field used for the "awaiting my approval" filter. */
    recipients?: string & keyof T;
  };
};

/**
 * Shared hook for quick filter handling across Jobs, Data Explorer, and Access Requests.
 */
export function useFilters<T extends object>({
  queryState,
  updateQueryState,
  currentUserId,
  fieldNames,
}: UseFiltersOptions<T>) {
  const creatorField = (fieldNames?.creator ?? "creator") as string & keyof T;
  const dateField = (fieldNames?.date ?? "created") as string & keyof T;
  const recipientsField = fieldNames?.recipients;

  const setFilters = useCallback(
    (
      updates: Array<{
        fieldName: string & keyof T;
        filter: Partial<FieldFilters> | null;
      }>,
    ) => {
      const newFilters = new Map(queryState.query.filters);
      for (const { fieldName, filter } of updates) {
        if (filter === null) {
          newFilters.delete(fieldName);
        } else {
          newFilters.set(fieldName, {
            valueList: filter.valueList ?? [],
            booleanValueList: filter.booleanValueList ?? [],
            fuzzyMatch: filter.fuzzyMatch ?? null,
            range: filter.range ?? [],
          });
        }
      }
      updateQueryState({
        ...queryState,
        query: { ...queryState.query, filters: newFilters },
        paging: { ...queryState.paging, offset: 0 },
      });
    },
    [queryState, updateQueryState],
  );

  const setFilter = useCallback(
    (fieldName: string & keyof T, filter: Partial<FieldFilters> | null) => {
      setFilters([{ fieldName, filter }]);
    },
    [setFilters],
  );

  /**
   * Set the creator filter to the given user, or pass `null` to clear it.
   * When `clearCounterpart` is true, activating this filter also clears the recipients filter.
   */
  const handleCreatorChange = useCallback(
    (
      userId: string | null,
      { clearCounterpart }: { clearCounterpart?: boolean } = {},
    ) => {
      const updates: Array<{
        fieldName: string & keyof T;
        filter: Partial<FieldFilters> | null;
      }> = [
        {
          fieldName: creatorField,
          filter: userId ? { valueList: [userId] } : null,
        },
      ];
      if (clearCounterpart && userId && recipientsField) {
        updates.push({ fieldName: recipientsField, filter: null });
      }
      setFilters(updates);
    },
    [setFilters, creatorField, recipientsField],
  );

  /** Set the date range filter to "today" or "this week", or pass `null` to clear it. */
  const handleDateRangeChange = useCallback(
    (filterName: "isToday" | "isWeek" | null) => {
      if (filterName) {
        const [start, end] = getDateRangeForQuery(filterName);
        setFilters([{ fieldName: dateField, filter: { range: [start, end] } }]);
      } else {
        setFilter(dateField, null);
      }
    },
    [setFilters, setFilter, dateField],
  );

  /**
   * Set the recipients filter to the given user, or pass `null` to clear it.
   * When `clearCounterpart` is true, activating this filter also clears the creator filter.
   */
  const handleRecipientsChange = useCallback(
    (
      userId: string | null,
      { clearCounterpart }: { clearCounterpart?: boolean } = {},
    ) => {
      if (!recipientsField) return;
      const updates: Array<{
        fieldName: string & keyof T;
        filter: Partial<FieldFilters> | null;
      }> = [
        {
          fieldName: recipientsField,
          filter: userId ? { valueList: [userId] } : null,
        },
      ];
      if (clearCounterpart && userId && creatorField) {
        updates.push({ fieldName: creatorField, filter: null });
      }
      setFilters(updates);
    },
    [setFilters, creatorField, recipientsField],
  );

  const onSetQuickFilter = useCallback(
    ({
      filter: { filterName, isSelected },
    }: {
      filter: { filterName: FilterName; isSelected: boolean };
    }) => {
      switch (filterName) {
        case "isMine":
          handleCreatorChange(
            isSelected && currentUserId ? currentUserId : null,
            { clearCounterpart: true },
          );
          break;
        case "isEditable":
          updateQueryState({
            ...queryState,
            isEditable: isSelected,
          });
          break;
        case "isToday":
        case "isWeek":
          handleDateRangeChange(isSelected ? filterName : null);
          break;
        case "isRequestsToMe":
          handleRecipientsChange(
            isSelected && currentUserId ? currentUserId : null,
            { clearCounterpart: true },
          );
          break;
      }
    },
    [
      currentUserId,
      handleCreatorChange,
      handleDateRangeChange,
      handleRecipientsChange,
      queryState,
      updateQueryState,
    ],
  );

  const quickFilterState: FilterState = useMemo(
    () => ({
      isMine: currentUserId
        ? queryState.query.filters.get(creatorField)?.valueList[0] ===
          currentUserId
        : false,
      isEditable: queryState.isEditable ?? false,
      isToday: isDateRangeToday(queryState.query.filters.get(dateField)?.range),
      isWeek: isDateRangeThisWeek(
        queryState.query.filters.get(dateField)?.range,
      ),
      isRequestsToMe:
        recipientsField && currentUserId
          ? queryState.query.filters.get(recipientsField)?.valueList[0] ===
            currentUserId
          : false,
    }),
    [
      queryState.query.filters,
      queryState.isEditable,
      currentUserId,
      creatorField,
      dateField,
      recipientsField,
    ],
  );

  return {
    setFilter,
    handleCreatorChange,
    handleDateRangeChange,
    onSetQuickFilter,
    quickFilterState,
  };
}
